/**
 * useShare — what the Share door does (plan `2026_10_25_wall_share.md` §§ 3-9). `Wall.tsx` calls it once
 * for the picture on screen — a done take, or the basis tile itself (operator 2026-11-01: the door stands on
 * every picture, not only on generated ones); `ShareDoor` is drawn from what it returns.
 *
 * The runners are plain functions over an injected `ShareDeps`, so the spec drives them with fakes and the
 * hook wires the browser's. Three rules shape them:
 *  - the picture is fetched into a `File` before the tap (`prepare`), so `navigator.share` runs inside the
 *    gesture's activation with the file already in hand;
 *  - the link's stamp (`POST /share`) runs in parallel with the share or the copy, never before it:
 *    awaiting a POST would spend the activation. The link is known without the server
 *    (`takeSharePath`), and a recipient cannot open it before the stamp lands;
 *  - a link is passed on only at a house whose theme says so (`share.link`); Ovid keeps the picture only;
 *  - a visitor on a take runs the keep-only methods and no stamp; the link row opens the sign-in card instead
 *    (plan `2026_10_25_toile_first_take_free.md` § 7). A pack tile's link is its public pack page: no stamp,
 *    no account.
 */
import {useCallback, useEffect, useRef, useState} from 'react'
import {track as trackEvent, type ShareSurface} from '@core/utils/analytics'
import {downloadUrl} from '@core/tools/ui/DownloadLink'
import {reencodeImage} from '@core/utils/imageCompress'
import type {Take, TileSummary} from '@core/wall/contract'
import {
    SHARE_LINES, doorMethods, failReasonFor, pickOpensSignIn, shareData, takeSharePath, tapAction,
    type ShareEnv, type ShareFailReason, type ShareMethod,
} from '@core/wall/share'
import {chainKeyOf, tileChainKey, type ChainKey} from './chains'
import type {HouseShare, HouseTheme} from './theme'
import type {ShareDoorProps} from './ShareDoor'

export type PostOutcome = 'ok' | 'unauthorized' | 'failed'

/** The picture the door shares. */
export type SharePicture = {
    imageKey: string
    imageUrl: string
    chain: ChainKey
    tileId: number | undefined
    /** The saved file's name after the house's `fileBase`. */
    stem: string
    /** The page that passes the picture on, root-relative; null where there is none. */
    path: string | null
    /** The take behind a take's page, which resolves only once stamped (`POST /share`) and only for an
     *  account. Null for a pack tile: its page is public, nothing to stamp, no account asked. */
    take: Take | null
}

/** A done take's picture; null while it has none. */
export function takePicture(theme: HouseTheme, take: Take): SharePicture | null {
    const key = take.status === 'done' ? take.image_key : null
    if (key === null) return null
    return {
        imageKey: key,
        imageUrl: theme.takeImageUrl(key),
        chain: chainKeyOf(take),
        tileId: take.root_tile_id ?? undefined,
        stem: key.slice(0, 8),
        path: takeSharePath(key),
        take,
    }
}

export function tilePicture(theme: HouseTheme, tile: TileSummary): SharePicture {
    return {
        imageKey: tile.image_key,
        imageUrl: theme.tileImageUrl(tile.image_key),
        chain: tileChainKey(tile.id),
        tileId: tile.id,
        stem: `${tile.pack_slug}-${tile.position + 1}`,
        path: theme.tileLabelHref(tile.pack_slug),
        take: null,
    }
}

export type ShareDeps = {
    share(data: ShareData): Promise<void>
    canShare(data: ShareData): boolean
    /** Takes the PNG as a promise: the clipboard item is built in the click, its bytes arrive after. */
    write(png: Promise<Blob>): Promise<void>
    writeText(text: string): Promise<void>
    download(url: string, basename: string): Promise<void>
    toPng(blob: Blob): Promise<Blob>
    post(takeId: string): Promise<PostOutcome>
}

export type ShareTrack = typeof trackEvent

/** Everything one press needs. */
export type ShareRun = {
    deps: ShareDeps
    track: ShareTrack
    house: string
    chain: string
    tileId: number | undefined
    surface: ShareSurface
    share: HouseShare
    /** The take to stamp; null for a picture whose page needs none. */
    takeId: string | null
    imageUrl: string
    basename: string
    /** The picture's page, absolute; null at a house without links, or for a picture that has no page. */
    link: string | null
    /** The prepared picture; null until the fetch settles. */
    file: File | null
    blob: () => Promise<Blob>
    notify: (line: string) => void
    openSignIn: () => void
}

function eventBase(run: ShareRun) {
    return {
        house: run.house, chain: run.chain, surface: run.surface,
        ...(run.tileId !== undefined ? {tile_id: run.tileId} : {}),
    }
}

function completed(run: ShareRun, method: ShareMethod) {
    run.track('share_completed', {...eventBase(run), method})
}

function failed(run: ShareRun, method: ShareMethod, reason: ShareFailReason) {
    run.track('share_failed', {house: run.house, chain: run.chain, surface: run.surface, method, reason})
}

/** `POST /share`, for a take's link only. A 401 opens sign-in; any other miss says `could not share`. */
async function stamp(run: ShareRun): Promise<void> {
    if (!run.share.link || run.link === null || run.takeId === null) return
    const outcome = await run.deps.post(run.takeId)
    if (outcome === 'unauthorized') run.openSignIn()
    else if (outcome === 'failed') run.notify(SHARE_LINES.failed)
}

export async function runNative(run: ShareRun): Promise<void> {
    void stamp(run)
    let data = shareData(run.file, run.link, run.share.words)
    if (run.file && !run.deps.canShare(data)) data = shareData(null, run.link, run.share.words)
    try {
        await run.deps.share(data)
        completed(run, 'native')
    } catch (err) {
        const reason = failReasonFor(err)
        failed(run, 'native', reason)
        if (reason !== 'cancelled') run.notify(SHARE_LINES.failed)
    }
}

export async function runDownload(run: ShareRun): Promise<void> {
    try {
        await run.deps.download(run.imageUrl, run.basename)
        completed(run, 'download')
    } catch {
        failed(run, 'download', 'fetch')
        run.notify(SHARE_LINES.failed)
    }
}

export async function runCopyImage(run: ShareRun): Promise<void> {
    const png = run.blob().then(run.deps.toPng)
    try {
        await run.deps.write(png)
        completed(run, 'copy_image')
        run.notify(SHARE_LINES.pictureCopied)
    } catch {
        failed(run, 'copy_image', 'clipboard')
        run.notify(SHARE_LINES.copyFailed)
    }
}

export async function runCopyLink(run: ShareRun): Promise<void> {
    if (run.link === null) return
    const stamping = stamp(run)
    try {
        await run.deps.writeText(run.link)
        completed(run, 'copy_link')
        run.notify(SHARE_LINES.linkCopied)
    } catch {
        failed(run, 'copy_link', 'clipboard')
        run.notify(SHARE_LINES.copyFailed)
    }
    await stamping
}

export function runMethod(method: ShareMethod, run: ShareRun): Promise<void> {
    switch (method) {
        case 'native': return runNative(run)
        case 'download': return runDownload(run)
        case 'copy_image': return runCopyImage(run)
        case 'copy_link': return runCopyLink(run)
    }
}

const BROWSER_DEPS: ShareDeps = {
    share: (data) => navigator.share(data),
    canShare: (data) => (typeof navigator.canShare === 'function' ? navigator.canShare(data) : true),
    write: (png) => navigator.clipboard.write([new ClipboardItem({'image/png': png})]),
    writeText: (text) => navigator.clipboard.writeText(text),
    download: downloadUrl,
    toPng: (blob) => reencodeImage(blob, 'image/png'),
    post: async (takeId) => {
        try {
            const res = await fetch('/share', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({take: takeId}),
            })
            if (res.status === 401) return 'unauthorized'
            return res.ok ? 'ok' : 'failed'
        } catch {
            return 'failed'
        }
    },
}

const NO_ENV: ShareEnv = {canShare: false, canShareFile: false, canCopyImage: false}

export function useShare({theme, signedIn, picture, setNotice, openSignIn}: {
    theme: HouseTheme
    signedIn: boolean
    /** The picture on screen (`armedView.sharePicture`), or null. */
    picture: SharePicture | null
    setNotice: (notice: {key: ChainKey; line: string}) => void
    openSignIn: (take: Take) => void
}): ShareDoorProps | null {
    const share = theme.share
    const [probe, setProbe] = useState<Pick<ShareEnv, 'canShare' | 'canCopyImage'>>(NO_ENV)
    const [prepared, setPrepared] = useState<{key: string; file: File} | null>(null)
    const cache = useRef(new Map<string, Promise<File | null>>())

    useEffect(() => {
        setProbe({
            canShare: typeof navigator.share === 'function',
            canCopyImage: typeof ClipboardItem !== 'undefined' && typeof navigator.clipboard?.write === 'function',
        })
    }, [])

    const imageKey = picture?.imageKey ?? null
    const imageUrl = picture?.imageUrl ?? null
    const basename = picture && share ? `${share.fileBase}-${picture.stem}` : ''

    const prepare = useCallback(() => {
        if (imageKey === null || imageUrl === null || cache.current.has(imageKey)) return
        const key = imageKey
        const ext = key.includes('.') ? key.slice(key.lastIndexOf('.')) : '.webp'
        const pending = (async () => {
            try {
                const res = await fetch(imageUrl)
                if (!res.ok) return null
                const blob = await res.blob()
                const file = new File([blob], `${basename}${ext}`, {type: blob.type || 'image/webp'})
                setPrepared({key, file})
                return file
            } catch {
                return null
            }
        })()
        cache.current.set(key, pending)
    }, [imageKey, imageUrl, basename])

    const file = prepared && prepared.key === imageKey ? prepared.file : null
    const env: ShareEnv = {
        ...probe,
        canShareFile: probe.canShare && file !== null
            && (typeof navigator.canShare !== 'function' || navigator.canShare({files: [file]})),
    }

    if (!share || !picture || imageKey === null || imageUrl === null) return null

    // A take's page needs an account: a visitor's runs on a take carry the picture alone — no link, no
    // stamp (`POST /share` refuses a visitor). A pack tile's page is public: anyone passes it on.
    const take = picture.take
    const linkOpen = signedIn || take === null
    const hasLink = share.link && picture.path !== null
    const runShare: HouseShare = {...share, link: hasLink && linkOpen}

    const makeRun = (surface: ShareSurface): ShareRun => ({
        deps: BROWSER_DEPS,
        track: trackEvent,
        house: theme.id,
        chain: picture.chain,
        tileId: picture.tileId,
        surface,
        share: runShare,
        takeId: take?.id ?? null,
        imageUrl,
        basename,
        link: runShare.link && picture.path ? window.location.origin + picture.path : null,
        file,
        blob: async () => {
            const ready = await cache.current.get(imageKey)
            if (!ready) throw new Error('fetch')
            return ready
        },
        notify: (line) => setNotice({key: picture.chain, line}),
        openSignIn: () => {
            if (take) openSignIn(take)
        },
    })

    return {
        imageKey,
        words: share.words,
        methods: doorMethods(env, hasLink, linkOpen),
        prepare,
        onTap: (surface) => {
            const action = tapAction(env, surface, runShare.link)
            const run = makeRun(surface)
            trackEvent('share_opened', {...eventBase(run), method: action})
            if (action === 'native') void runNative(run)
            return action
        },
        onPick: (method, surface) => {
            if (take && pickOpensSignIn(method, linkOpen)) {
                openSignIn(take)
                return
            }
            void runMethod(method, makeRun(surface))
        },
    }
}
